// Préférences locales (zoom, affichage) persistées dans localStorage.
// Pur — aucune dépendance ; sûr si le storage est indisponible.

const KEY = "agencyos_prefs";

/** Réglage de thème : suit le système, ou force clair/sombre. */
export type ThemePref = "auto" | "light" | "dark";

/** « Monde » actif : les chats (messageries) ou l'e-mail — deux surfaces du rail. */
export type World = "messaging" | "email";

export interface Prefs {
  zoom: number;
  showArchived: boolean;
  world: World; // dernier monde choisi (chat vs e-mail), restauré au boot
  // — Apparence —
  theme: ThemePref; // bascule clair/sombre (classes .theme-* sur <html>)
  compactList: boolean; // liste de conversations dense
  showPreviews: boolean; // aperçu du dernier message dans la liste
  showTimestamps: boolean; // horodatage dans les bulles
  reduceTransparency: boolean; // désactive le flou/verre (lisibilité, perf)
  // — Notifications —
  notifications: boolean; // notifications bureau
  notifSound: boolean; // son à la réception
}

const DEFAULTS: Prefs = {
  zoom: 1,
  showArchived: false,
  world: "messaging",
  theme: "auto",
  compactList: false,
  showPreviews: true,
  showTimestamps: true,
  reduceTransparency: false,
  notifications: true,
  notifSound: true,
};

/** Applique les préférences d'apparence globales (classes sur <html>/<body>). */
export function applyPrefs(p: Prefs): void {
  // Thème : les classes .theme-* sur <html> PRIMENT sur prefers-color-scheme
  // (cf css/tokens.css) ; en « auto » on n'en pose aucune.
  const r = document.documentElement.classList;
  r.toggle("theme-light", p.theme === "light");
  r.toggle("theme-dark", p.theme === "dark");
  const b = document.body.classList;
  b.toggle("reduce-transparency", p.reduceTransparency);
  b.toggle("hide-timestamps", !p.showTimestamps);
}

export function loadPrefs(): Prefs {
  try {
    return { ...DEFAULTS, ...(JSON.parse(localStorage.getItem(KEY) || "{}") as Partial<Prefs>) };
  } catch {
    return { ...DEFAULTS };
  }
}

export function savePrefs(patch: Partial<Prefs>): void {
  try {
    localStorage.setItem(KEY, JSON.stringify({ ...loadPrefs(), ...patch }));
  } catch {
    /* storage indisponible — on ignore */
  }
}

/** Applique le zoom sur la racine du document (WKWebView/Chromium supportent `zoom`). */
export function applyZoom(zoom: number): void {
  document.documentElement.style.setProperty("zoom", String(zoom));
}

/** Borne le zoom à une plage raisonnable. */
export function clampZoom(z: number): number {
  return Math.min(2, Math.max(0.6, Math.round(z * 10) / 10));
}
